import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Settings,
  ShieldCheck,
  MessageCircle,
  Mail,
  Camera,
  ThumbsUp,
  Music2,
  UserX
} from 'lucide-react';
import { useSocket } from '../hooks/useSocket';
import { sessionSettings } from '../utils/roles';
import { useDialog } from '../hooks/useDialog';
import toast from 'react-hot-toast';

// Apple Music genre names as they appear in catalog genreNames
const APPLE_GENRES = [
  'Hip-Hop/Rap', 'Pop', 'R&B/Soul', 'Rock', 'Country', 'Dance',
  'Electronic', 'Latin', 'Alternative', 'Jazz', 'Christian & Gospel',
  'Reggae', 'K-Pop', 'Afrobeats'
];

const Toggle = ({ label, description, icon: Icon, value, onChange }) => (
  <div className="flex items-center justify-between py-3 border-b border-gray-700 last:border-b-0">
    <div className="flex items-start space-x-3 min-w-0 pr-3">
      <Icon className="w-5 h-5 text-gray-400 mt-0.5 shrink-0" />
      <div className="min-w-0">
        <p className="text-white text-sm font-medium">{label}</p>
        <p className="text-gray-400 text-xs">{description}</p>
      </div>
    </div>
    <button
      onClick={() => onChange(!value)}
      className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${
        value ? 'bg-purple-600' : 'bg-gray-600'
      }`}
      role="switch"
      aria-checked={value}
      aria-label={label}
    >
      <span
        className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
          value ? 'translate-x-5' : ''
        }`}
      />
    </button>
  </div>
);

const HostSettingsPanel = ({ session, onClose }) => {
  const { hostAction } = useSocket();
  const settings = sessionSettings(session);
  const [artistInput, setArtistInput] = useState('');
  const isApple = session.musicService === 'apple';
  const dialogRef = useDialog(onClose);

  const update = (key, value, message) => {
    hostAction('updateSettings', null, null, { settings: { [key]: value } });
    if (message) toast.success(message, { duration: 2500 });
  };

  const addBlockedArtist = () => {
    const name = artistInput.trim();
    if (!name) return;
    const existing = settings.blockedArtists || [];
    if (existing.some((a) => a.toLowerCase() === name.toLowerCase())) {
      toast.error('That artist is already blocked');
      return;
    }
    update('blockedArtists', [...existing, name], `Blocked ${name}`);
    setArtistInput('');
  };

  const removeBlockedArtist = (name) => {
    update(
      'blockedArtists',
      (settings.blockedArtists || []).filter((a) => a !== name),
      `Unblocked ${name}`
    );
  };

  const toggleGenre = (genre) => {
    const current = settings.genreRestrictions || [];
    const next = current.includes(genre)
      ? current.filter((g) => g !== genre)
      : [...current, genre];
    update('genreRestrictions', next);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
        onClick={onClose}
        role="dialog"
        aria-modal="true"
        aria-label="Session controls"
      >
        <motion.div
          ref={dialogRef}
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-gray-800 rounded-lg w-full max-w-lg max-h-[85vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-700 shrink-0">
            <h2 className="text-lg sm:text-xl font-semibold text-white flex items-center">
              <Settings className="w-5 h-5 mr-2" />
              Session Controls
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
              aria-label="Close settings"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 sm:p-6 min-h-0">
            {/* Music rules */}
            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-1">
              Music
            </h3>
            <Toggle
              label="Require approval"
              description="Guest songs wait for your (or a co-host's) approval"
              icon={ShieldCheck}
              value={settings.requireApproval}
              onChange={(v) =>
                update('requireApproval', v, v ? 'Approval gate on' : 'Songs now go straight to the playlist')
              }
            />
            <Toggle
              label="Allow explicit songs"
              description="Off = clean versions only (applies to everyone but you)"
              icon={Music2}
              value={settings.explicitAllowed}
              onChange={(v) =>
                update('explicitAllowed', v, v ? 'Explicit songs allowed' : 'Clean music only')
              }
            />
            <Toggle
              label="Guest voting"
              description="Let guests up/down-vote songs in the queue"
              icon={ThumbsUp}
              value={settings.allowGuestVoting}
              onChange={(v) => update('allowGuestVoting', v)}
            />

            {/* Blocked artists */}
            <div className="py-3 border-b border-gray-700">
              <div className="flex items-center space-x-3 mb-2">
                <UserX className="w-5 h-5 text-gray-400 shrink-0" />
                <div>
                  <p className="text-white text-sm font-medium">Blocked artists</p>
                  <p className="text-gray-400 text-xs">Their songs can't be suggested or added</p>
                </div>
              </div>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={artistInput}
                  onChange={(e) => setArtistInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addBlockedArtist()}
                  placeholder="Artist name"
                  className="input-field flex-1 text-sm"
                  maxLength={100}
                />
                <button onClick={addBlockedArtist} className="btn-secondary text-sm">
                  Block
                </button>
              </div>
              {(settings.blockedArtists || []).length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {settings.blockedArtists.map((artist) => (
                    <span
                      key={artist}
                      className="inline-flex items-center bg-red-600/20 text-red-300 text-xs px-2 py-1 rounded-full"
                    >
                      {artist}
                      <button
                        onClick={() => removeBlockedArtist(artist)}
                        className="ml-1.5 hover:text-white"
                        aria-label={`Unblock ${artist}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Genres — Apple Music sessions only */}
            {isApple && (
              <div className="py-3 border-b border-gray-700">
                <div className="flex items-center space-x-3 mb-2">
                  <Music2 className="w-5 h-5 text-gray-400 shrink-0" />
                  <div>
                    <p className="text-white text-sm font-medium">Allowed genres</p>
                    <p className="text-gray-400 text-xs">
                      Select none to allow everything. Apple Music sessions only.
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {APPLE_GENRES.map((genre) => {
                    const active = (settings.genreRestrictions || []).includes(genre);
                    return (
                      <button
                        key={genre}
                        onClick={() => toggleGenre(genre)}
                        className={`text-xs px-2.5 py-1 rounded-full transition-colors ${
                          active
                            ? 'bg-purple-600 text-white'
                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        }`}
                      >
                        {genre}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Communication */}
            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-1 mt-5">
              Communication
            </h3>
            <Toggle
              label="Group chat"
              description="Hidden for everyone when off; history is kept"
              icon={MessageCircle}
              value={settings.chatEnabled}
              onChange={(v) => update('chatEnabled', v, v ? 'Chat is back on' : 'Chat turned off')}
            />
            <Toggle
              label="Direct messages"
              description="Private messages between guests"
              icon={Mail}
              value={settings.dmsEnabled}
              onChange={(v) => update('dmsEnabled', v, v ? 'DMs are back on' : 'DMs turned off')}
            />
            <Toggle
              label="Photo sharing"
              description="The Photos tab and photos in chat"
              icon={Camera}
              value={settings.photosEnabled}
              onChange={(v) => update('photosEnabled', v, v ? 'Photos are back on' : 'Photo sharing turned off')}
            />
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default HostSettingsPanel;
